import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { apiFetch } from "./config/apiConfig";

const StoryDetailPage = ({ user, logout, renderUserInfo }) => {
  const { storyId } = useParams();
  const navigate = useNavigate();

  const [story, setStory] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [comparativeAnalysis, setComparativeAnalysis] = useState(null);
  const [comparativeAnalysisLoading, setComparativeAnalysisLoading] =
    useState(false);

  useEffect(() => {
    if (storyId) {
      fetchStoryDetails();
      fetchStoryAnalysis();
      fetchComparativeAnalysis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId]);

  const fetchStoryDetails = async () => {
    try {
      setLoading(true);
      const response = await apiFetch(`/news/stories/${storyId}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch story: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setStory(data.story);
      } else {
        throw new Error(data.error || "Failed to load story");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStoryAnalysis = async () => {
    try {
      setAnalysisLoading(true);
      const response = await apiFetch(`/news/stories/${storyId}/analysis`);

      if (!response.ok) {
        throw new Error(`Failed to fetch analysis: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setAnalysis(data.analysis);
      } else {
        throw new Error(data.error || "Failed to load analysis");
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Analysis fetch error:", err);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const fetchComparativeAnalysis = async () => {
    try {
      setComparativeAnalysisLoading(true);
      const sessionToken = localStorage.getItem("sessionToken");

      const response = await apiFetch(
        `/news/stories/${storyId}/comparative-analysis`,
        {
          headers: {
            ...(sessionToken && { Authorization: `Bearer ${sessionToken}` }),
          },
        }
      );

      if (response && response.ok) {
        const data = await response.json();
        if (data.success) {
          setComparativeAnalysis(data.analysis);
        }
      }
    } catch (err) {
      // Silently handle fetch errors in tests and development
      if (process.env.NODE_ENV !== "test") {
        // eslint-disable-next-line no-console
        console.error("Comparative analysis fetch error:", err);
      }
    } finally {
      setComparativeAnalysisLoading(false);
    }
  };

  // Enhanced function to get article metadata from KV storage first
  const getArticleMetadata = async (article) => {
    try {
      const domain = extractDomain(article.url || article.link || "");
      if (!domain) return null;

      const sessionToken = localStorage.getItem("sessionToken");
      const response = await apiFetch(`/article-metadata/${domain}`, {
        headers: {
          ...(sessionToken && { Authorization: `Bearer ${sessionToken}` }),
        },
      });

      if (response.ok) {
        const metadata = await response.json();
        return {
          bias_score_numeric: metadata.bias_score,
          credibility_score_numeric: metadata.credibility_score,
          bias_source: metadata.bias_source,
          credibility_source: metadata.credibility_source,
        };
      }
    } catch (err) {
      // Silently handle errors
    }
    return null;
  };

  // Enhanced function to get AI summary
  const getAISummary = async (article) => {
    try {
      const sessionToken = localStorage.getItem("sessionToken");
      const response = await apiFetch(`/ai-summary`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(sessionToken && { Authorization: `Bearer ${sessionToken}` }),
        },
        body: JSON.stringify({
          title: article.title || "",
          content: article.description || "",
          url: article.url || article.link || "",
        }),
      });

      if (response.ok) {
        const data = await response.json();
        return data.summary || data.ai_analysis || null;
      }
    } catch (err) {
      // Silently handle errors
    }
    return null;
  };

  const enhanceArticleWithAnalysis = async (article, index) => {
    try {
      // First try to get metadata from KV storage
      const metadata = await getArticleMetadata(article);
      if (metadata) {
        // Get AI summary separately
        const aiSummary = await getAISummary(article);

        return {
          ...article,
          enhanced_bias_score: metadata.bias_score_numeric,
          enhanced_credibility_score: metadata.credibility_score_numeric,
          ai_summary: aiSummary || article.description || "",
          bias_source: metadata.bias_source,
          credibility_source: metadata.credibility_source,
        };
      }

      // Fallback to full bias-check endpoint if no metadata found
      const sessionToken = localStorage.getItem("sessionToken");
      const response = await apiFetch("/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(sessionToken && { Authorization: `Bearer ${sessionToken}` }),
        },
        body: JSON.stringify({
          url: article.url || article.link,
          text: `Title: ${article.title}\nSource: ${article.source}\nDescription: ${article.description || ""}`,
          ...(user?.email && { email: user.email }),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        return {
          ...article,
          enhanced_bias_score: data.bias_score_numeric,
          enhanced_credibility_score: data.credibility_score_numeric,
          ai_summary: data.ai_analysis,
          bias_source: data.bias_source,
          credibility_source: data.credibility_source,
        };
      }
    } catch (err) {
      // Silently handle errors during article enhancement
    }
    return article;
  };

  // Extract domain from URL
  const extractDomain = (url) => {
    try {
      if (!url) return "";
      const urlObj = new URL(url.startsWith("http") ? url : `https://${url}`);
      return urlObj.hostname.replace(/^www\./, "");
    } catch {
      return "";
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return "Unknown time";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Unknown time";

      const now = new Date();
      const diffMs = now - date;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffHours / 24);

      if (diffDays > 0) {
        return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
      } else if (diffHours > 0) {
        return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
      } else {
        return "Recently";
      }
    } catch (err) {
      return "Unknown time";
    }
  };

  // Format bias score to match backend getBiasDescription function
  const formatBiasScore = (biasScore) => {
    if (biasScore === null || biasScore === undefined) {
      return "Unknown";
    }

    const numScore = parseFloat(biasScore);
    if (isNaN(numScore)) return "Unknown";

    if (numScore <= -0.7) return "Strongly Left Leaning";
    if (numScore <= -0.3) return "Left Leaning";
    if (numScore <= -0.1) return "Slightly Left Leaning";
    if (numScore <= 0.1) return "Center/Neutral";
    if (numScore <= 0.3) return "Slightly Right Leaning";
    if (numScore <= 0.7) return "Right Leaning";
    return "Strongly Right Leaning";
  };

  // Format bias category (for RSS articles)
  const formatBiasCategory = (biasCategory) => {
    if (!biasCategory) return "Unknown";

    const category = biasCategory.toLowerCase();
    if (category === "left" || category === "left-leaning")
      return "Left Leaning";
    if (category === "right" || category === "right-leaning")
      return "Right Leaning";
    if (
      category === "center" ||
      category === "center-leaning" ||
      category === "centrist"
    )
      return "Center/Neutral";
    return "Unknown";
  };

  // Get bias color based on formatted bias score
  const getBiasColorFromScore = (biasScore) => {
    if (biasScore === null || biasScore === undefined) {
      return "text-gray-600 bg-gray-50";
    }

    const numScore = parseFloat(biasScore);
    if (isNaN(numScore)) return "text-gray-600 bg-gray-50";

    if (numScore <= -0.1) return "text-blue-600 bg-blue-50"; // Left leaning
    if (numScore >= 0.1) return "text-red-600 bg-red-50"; // Right leaning
    return "text-gray-600 bg-gray-50"; // Center
  };

  // Get bias color based on bias category
  const getBiasColorFromCategory = (biasCategory) => {
    if (!biasCategory) return "text-gray-600 bg-gray-50";

    const category = biasCategory.toLowerCase();
    if (category === "left" || category === "left-leaning")
      return "text-blue-600 bg-blue-50";
    if (category === "right" || category === "right-leaning")
      return "text-red-600 bg-red-50";
    return "text-gray-600 bg-gray-50"; // Center
  };

  // Format credibility score consistently
  const formatCredibilityScore = (credibilityScore) => {
    if (credibilityScore === null || credibilityScore === undefined) {
      return "Unknown";
    }

    const numScore = parseFloat(credibilityScore);
    if (isNaN(numScore)) return "Unknown";

    // Convert decimal to percentage for display
    const percentage = Math.round(numScore * 100);

    if (numScore >= 0.8) return `${percentage}% (Excellent)`;
    if (numScore >= 0.6) return `${percentage}% (Good)`;
    if (numScore >= 0.4) return `${percentage}% (Fair)`;
    return `${percentage}% (Poor)`;
  };

  // Get credibility color based on score
  const getCredibilityColor = (credibilityScore) => {
    if (credibilityScore === null || credibilityScore === undefined) {
      return "text-gray-600 bg-gray-50";
    }

    const numScore = parseFloat(credibilityScore);
    if (isNaN(numScore)) return "text-gray-600 bg-gray-50";

    if (numScore >= 0.8) return "text-green-600 bg-green-50"; // Excellent
    if (numScore >= 0.6) return "text-yellow-600 bg-yellow-50"; // Good
    if (numScore >= 0.4) return "text-orange-600 bg-orange-50"; // Fair
    return "text-red-600 bg-red-50"; // Poor
  };

  // Tooltip component for sentiment analysis explanation
  const SentimentTooltip = ({ children }) => {
    const [showTooltip, setShowTooltip] = useState(false);

    return (
      <div className="relative inline-block">
        <div
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className="cursor-help"
        >
          {children}
        </div>
        {showTooltip && (
          <div className="absolute z-10 w-80 p-3 bg-gray-900 text-white text-sm rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2">
            <div className="font-medium mb-2">Sentiment Analysis Explained</div>
            <div className="text-xs space-y-1">
              <p>
                <strong>Positive:</strong> Articles emphasize benefits,
                optimism, or favorable outcomes
              </p>
              <p>
                <strong>Negative:</strong> Articles focus on problems,
                criticism, or unfavorable aspects
              </p>
              <p>
                <strong>Neutral:</strong> Articles present information
                objectively without strong emotional tone
              </p>
              <p className="mt-2 text-gray-300">
                This analysis helps identify how different political
                perspectives frame the same story.
              </p>
            </div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
          </div>
        )}
      </div>
    );
  };

  SentimentTooltip.propTypes = {
    children: PropTypes.node.isRequired,
  };

  const renderBiasDistribution = (biasDistribution) => {
    const total =
      biasDistribution.left + biasDistribution.center + biasDistribution.right;
    if (total === 0) return null;

    const leftPercent = (biasDistribution.left / total) * 100;
    const centerPercent = (biasDistribution.center / total) * 100;
    const rightPercent = (biasDistribution.right / total) * 100;

    return (
      <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
        <div className="h-full flex">
          {leftPercent > 0 && (
            <div
              className="bg-blue-500 transition-all duration-300"
              style={{ width: `${leftPercent}%` }}
              title={`Left: ${biasDistribution.left} articles (${leftPercent.toFixed(1)}%)`}
            />
          )}
          {centerPercent > 0 && (
            <div
              className="bg-gray-400 transition-all duration-300"
              style={{ width: `${centerPercent}%` }}
              title={`Center: ${biasDistribution.center} articles (${centerPercent.toFixed(1)}%)`}
            />
          )}
          {rightPercent > 0 && (
            <div
              className="bg-red-500 transition-all duration-300"
              style={{ width: `${rightPercent}%` }}
              title={`Right: ${biasDistribution.right} articles (${rightPercent.toFixed(1)}%)`}
            />
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading story details...</p>
        </div>
      </div>
    );
  }

  if (error || !story) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
            <h2 className="text-lg font-medium text-red-800 mb-2">
              Story Not Found
            </h2>
            <p className="text-red-600 mb-4">
              {error || "The requested story could not be found."}
            </p>
            <button
              onClick={() => navigate("/news")}
              className="bg-red-100 text-red-800 px-4 py-2 rounded-md hover:bg-red-200 transition-colors"
            >
              ← Back to News
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate("/news")}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              ← Back to News
            </button>
            {renderUserInfo && renderUserInfo()}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Story Header */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                🧠 {Math.round((story.semantic_confidence || 0) * 100)}%
                Confidence
              </span>
              {analysis?.topic_tags?.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                >
                  {tag}
                </span>
              ))}
            </div>
            <span className="text-sm text-gray-500">
              {story.article_count} articles • Updated{" "}
              {formatTimeAgo(story.updated_at)}
            </span>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {story.title}
          </h1>

          {/* AI Summary */}
          {analysisLoading ? (
            <div className="bg-indigo-50 rounded-lg p-4 mb-6">
              <div className="animate-pulse">
                <div className="h-4 bg-indigo-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-indigo-200 rounded w-1/2"></div>
              </div>
            </div>
          ) : story.ai_summary || analysis?.ai_summary ? (
            <div className="bg-indigo-50 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-medium text-indigo-900 mb-2">
                🤖 AI Summary
              </h3>
              <p className="text-indigo-800 leading-relaxed">
                {story.ai_summary || analysis?.ai_summary}
              </p>
            </div>
          ) : null}

          {/* Bias Distribution */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium text-gray-900">
                Political Perspective Distribution
              </h3>
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <span className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                  Left ({story.bias_distribution?.left || 0})
                </span>
                <span className="flex items-center">
                  <div className="w-3 h-3 bg-gray-400 rounded-full mr-2"></div>
                  Center ({story.bias_distribution?.center || 0})
                </span>
                <span className="flex items-center">
                  <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                  Right ({story.bias_distribution?.right || 0})
                </span>
              </div>
            </div>
            {renderBiasDistribution(
              story.bias_distribution || { left: 0, center: 0, right: 0 }
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Analysis Insights */}
            {analysis && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">
                  🔍 Analysis Insights
                </h2>

                {/* Sentiment Analysis */}
                {analysis.sentiment_analysis && (
                  <div className="mb-6">
                    <SentimentTooltip>
                      <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                        Sentiment Analysis
                        <svg
                          className="w-4 h-4 ml-2 text-gray-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </h3>
                    </SentimentTooltip>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-sm text-gray-600 mb-1">
                          Overall
                        </div>
                        <div
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            analysis.sentiment_analysis.overall_sentiment ===
                            "positive"
                              ? "bg-green-100 text-green-800"
                              : analysis.sentiment_analysis
                                    .overall_sentiment === "negative"
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {analysis.sentiment_analysis.overall_sentiment}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-gray-600 mb-1">Left</div>
                        <div
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            analysis.sentiment_analysis.left_sentiment ===
                            "positive"
                              ? "bg-green-100 text-green-800"
                              : analysis.sentiment_analysis.left_sentiment ===
                                  "negative"
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {analysis.sentiment_analysis.left_sentiment}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-gray-600 mb-1">Center</div>
                        <div
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            analysis.sentiment_analysis.center_sentiment ===
                            "positive"
                              ? "bg-green-100 text-green-800"
                              : analysis.sentiment_analysis.center_sentiment ===
                                  "negative"
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {analysis.sentiment_analysis.center_sentiment}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-gray-600 mb-1">Right</div>
                        <div
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            analysis.sentiment_analysis.right_sentiment ===
                            "positive"
                              ? "bg-green-100 text-green-800"
                              : analysis.sentiment_analysis.right_sentiment ===
                                  "negative"
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {analysis.sentiment_analysis.right_sentiment}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Common Ground */}
                {analysis.common_ground &&
                  analysis.common_ground.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-3">
                        🤝 Common Ground
                      </h3>
                      <div className="space-y-2">
                        {analysis.common_ground.map((point, index) => (
                          <div key={index} className="flex items-start">
                            <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <p className="text-gray-700">{point}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Key Differences - Enhanced with Comparative Analysis */}
                {(comparativeAnalysis ||
                  (analysis.key_differences &&
                    analysis.key_differences.length > 0)) && (
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                      <svg
                        className="w-5 h-5 mr-2 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                        />
                      </svg>
                      ⚡ Key Differences
                    </h3>

                    {comparativeAnalysisLoading ? (
                      <div className="animate-pulse space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                      </div>
                    ) : comparativeAnalysis ? (
                      (() => {
                        // Parse the comparative analysis into perspective sections
                        // This parsing logic matches the bias checker in App.js
                        const parseComparativeAnalysis = (text) => {
                          const sections = [];
                          const lines = text.split("\n");
                          let currentSection = null;

                          for (const line of lines) {
                            const trimmedLine = line.trim();
                            if (!trimmedLine) continue;

                            // Handle format: "LEFT:" or "Left:"
                            if (
                              trimmedLine.match(
                                /^(LEFT|CENTER|RIGHT|Left|Center|Right):/i
                              )
                            ) {
                              if (currentSection) sections.push(currentSection);

                              const [perspective, ...contentParts] =
                                trimmedLine.split(":");
                              currentSection = {
                                perspective: perspective.trim().toUpperCase(),
                                points:
                                  contentParts.length > 0
                                    ? [contentParts.join(":").trim()]
                                    : [],
                              };
                            }
                            // Handle format: "- Left-leaning sources..." or "- Center/neutral sources..."
                            else if (
                              trimmedLine.match(
                                /^-\s*(Left-leaning|Center\/neutral|Right-leaning|Left|Center|Right)/i
                              )
                            ) {
                              if (currentSection) sections.push(currentSection);

                              let perspective = "CENTER";
                              if (trimmedLine.match(/Left-leaning|Left/i)) {
                                perspective = "LEFT";
                              } else if (
                                trimmedLine.match(/Right-leaning|Right/i)
                              ) {
                                perspective = "RIGHT";
                              }

                              // Extract content after the perspective identifier
                              const content = trimmedLine
                                .replace(
                                  /^-\s*(Left-leaning|Center\/neutral|Right-leaning)\s*sources?\s*/i,
                                  ""
                                )
                                .trim();

                              currentSection = {
                                perspective: perspective,
                                points: content ? [content] : [],
                              };
                            }
                            // Handle regular bullet points
                            else if (
                              trimmedLine.startsWith("-") &&
                              currentSection
                            ) {
                              currentSection.points.push(
                                trimmedLine.substring(1).trim()
                              );
                            } else if (
                              currentSection &&
                              currentSection.points.length === 0
                            ) {
                              // First line after perspective header
                              currentSection.points.push(trimmedLine);
                            } else if (currentSection) {
                              // Continue previous point
                              const lastIndex =
                                currentSection.points.length - 1;
                              if (lastIndex >= 0) {
                                currentSection.points[lastIndex] +=
                                  " " + trimmedLine;
                              }
                            }
                          }

                          if (currentSection) sections.push(currentSection);
                          return sections;
                        };

                        const sections =
                          parseComparativeAnalysis(comparativeAnalysis);

                        const getPerspectiveColor = (perspective) => {
                          switch (perspective) {
                            case "LEFT":
                              return {
                                bg: "bg-blue-50",
                                border: "border-blue-400",
                                text: "text-blue-900",
                                icon: "←",
                              };
                            case "CENTER":
                              return {
                                bg: "bg-green-50",
                                border: "border-green-400",
                                text: "text-green-900",
                                icon: "⚖️",
                              };
                            case "RIGHT":
                              return {
                                bg: "bg-red-50",
                                border: "border-red-400",
                                text: "text-red-900",
                                icon: "→",
                              };
                            default:
                              return {
                                bg: "bg-gray-50",
                                border: "border-gray-400",
                                text: "text-gray-900",
                                icon: "•",
                              };
                          }
                        };

                        return (
                          <div className="p-6 bg-white rounded-lg border border-neutral-200 shadow-sm">
                            <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
                              <svg
                                className="w-5 h-5 mr-2 text-neutral-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                                />
                              </svg>
                              Media bias on this story
                            </h3>
                            <div className="space-y-4">
                              {sections.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                  {/* Ensure we have all three perspectives in the right order */}
                                  {["LEFT", "CENTER", "RIGHT"].map(
                                    (perspective) => {
                                      const section = sections.find(
                                        (s) => s.perspective === perspective
                                      );
                                      const colors =
                                        getPerspectiveColor(perspective);

                                      return (
                                        <div
                                          key={perspective}
                                          className={`p-4 ${colors.bg} rounded-lg border-l-4 ${colors.border} min-h-[200px]`}
                                        >
                                          <h4
                                            className={`text-sm font-semibold ${colors.text} mb-3 flex items-center justify-center text-center`}
                                          >
                                            <span className="mr-2 text-lg">
                                              {colors.icon}
                                            </span>
                                            {perspective}-LEANING
                                          </h4>
                                          <div className="space-y-2">
                                            {section ? (
                                              section.points.map(
                                                (point, pointIndex) => (
                                                  <div
                                                    key={pointIndex}
                                                    className={`${colors.text.replace("900", "800")} text-sm leading-relaxed`}
                                                  >
                                                    {point}
                                                  </div>
                                                )
                                              )
                                            ) : (
                                              <div
                                                className={`${colors.text.replace("900", "600")} text-sm italic text-center`}
                                              >
                                                No {perspective.toLowerCase()}
                                                -leaning analysis available
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              ) : (
                                // Fallback if parsing fails
                                <div className="p-4 bg-emerald-50 rounded-lg border-l-4 border-emerald-400">
                                  <h3 className="text-sm font-semibold text-emerald-900 mb-3 flex items-center">
                                    <svg
                                      className="w-4 h-4 mr-2"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    Comparative Analysis
                                  </h3>
                                  <div className="text-emerald-800 text-sm leading-relaxed whitespace-pre-wrap">
                                    {comparativeAnalysis}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()
                    ) : analysis.key_differences ? (
                      <div className="space-y-2">
                        {analysis.key_differences.map((difference, index) => (
                          <div key={index} className="flex items-start">
                            <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <p className="text-gray-700">{difference}</p>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            )}

            {/* Enhanced Articles List */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                📰 All Articles ({story.articles?.length || 0})
              </h2>

              <div className="space-y-4">
                {story.articles?.map((article, index) => {
                  // Use metadata from story first, then enhanced data, then fallback to unknown
                  const biasScore =
                    article.bias_score !== null &&
                    article.bias_score !== undefined
                      ? article.bias_score
                      : article.enhanced_bias_score !== undefined
                        ? article.enhanced_bias_score
                        : null;

                  const credibilityScore =
                    article.credibility_score !== null &&
                    article.credibility_score !== undefined
                      ? article.credibility_score
                      : article.enhanced_credibility_score !== undefined
                        ? article.enhanced_credibility_score
                        : null;

                  // Use bias category for RSS articles when numeric score isn't available
                  const hasBiasScore =
                    biasScore !== null && biasScore !== undefined;
                  const biasCategory = article.bias_category || "center";

                  const displayTitle =
                    article.title || article.source || "Untitled Article";

                  // Clean up description/summary - remove HTML and use proper summary
                  const cleanDescription = (desc) => {
                    if (!desc) return "";
                    // Remove HTML tags and entities
                    return desc
                      .replace(/<[^>]*>/g, "")
                      .replace(/&[a-zA-Z0-9#]+;/g, "")
                      .replace(/\s+/g, " ")
                      .trim();
                  };

                  const displaySummary =
                    article.ai_summary ||
                    cleanDescription(article.description) ||
                    "";

                  // Determine if we need enhancement
                  const needsEnhancement =
                    (!hasBiasScore && !biasCategory) ||
                    credibilityScore === null ||
                    !displaySummary;

                  return (
                    <div
                      key={index}
                      className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-medium text-gray-900 flex-1 pr-4">
                          {article.url || article.link ? (
                            <a
                              href={article.url || article.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:text-indigo-600 transition-colors"
                            >
                              {displayTitle}
                            </a>
                          ) : (
                            displayTitle
                          )}
                        </h3>
                        <div className="flex items-center space-x-2 flex-shrink-0">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              hasBiasScore
                                ? getBiasColorFromScore(biasScore)
                                : getBiasColorFromCategory(biasCategory)
                            }`}
                          >
                            {hasBiasScore
                              ? formatBiasScore(biasScore)
                              : formatBiasCategory(biasCategory)}
                          </span>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              credibilityScore !== null &&
                              credibilityScore !== undefined
                                ? getCredibilityColor(credibilityScore)
                                : "text-gray-600 bg-gray-50"
                            }`}
                          >
                            {formatCredibilityScore(credibilityScore)}
                          </span>
                        </div>
                      </div>

                      {displaySummary && (
                        <p className="text-gray-600 text-sm mb-2 leading-relaxed">
                          {displaySummary}
                        </p>
                      )}

                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{article.source || "Unknown Source"}</span>
                        <span>
                          {formatTimeAgo(
                            article.published_at || article.pubDate
                          )}
                        </span>
                      </div>

                      {/* Enhance Article Button - only show if needed */}
                      {needsEnhancement && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <button
                            onClick={async () => {
                              const enhanced = await enhanceArticleWithAnalysis(
                                article,
                                index
                              );
                              if (enhanced !== article) {
                                // Update the article in state
                                setStory((prevStory) => ({
                                  ...prevStory,
                                  articles: prevStory.articles.map((a, i) =>
                                    i === index ? enhanced : a
                                  ),
                                }));
                              }
                            }}
                            className="text-xs text-indigo-600 hover:text-indigo-800 transition-colors"
                          >
                            🔍 Enhance with AI Analysis
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Credibility Scores */}
            {analysis?.credibility_scores && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  🏆 Credibility Scores
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Overall</span>
                    <span
                      className={`px-2 py-1 rounded text-sm font-medium ${getCredibilityColor(analysis.credibility_scores.overall)}`}
                    >
                      {analysis.credibility_scores.overall}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Left Perspective</span>
                    <span
                      className={`px-2 py-1 rounded text-sm font-medium ${getCredibilityColor(analysis.credibility_scores.left_perspective)}`}
                    >
                      {analysis.credibility_scores.left_perspective}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Center Perspective</span>
                    <span
                      className={`px-2 py-1 rounded text-sm font-medium ${getCredibilityColor(analysis.credibility_scores.center_perspective)}`}
                    >
                      {analysis.credibility_scores.center_perspective}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Right Perspective</span>
                    <span
                      className={`px-2 py-1 rounded text-sm font-medium ${getCredibilityColor(analysis.credibility_scores.right_perspective)}`}
                    >
                      {analysis.credibility_scores.right_perspective}%
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Story Statistics */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                📊 Story Statistics
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Articles:</span>
                  <span className="font-medium">{story.article_count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Topic:</span>
                  <span className="font-medium">{story.topic}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Updated:</span>
                  <span className="font-medium">
                    {formatTimeAgo(story.updated_at)}
                  </span>
                </div>
              </div>
            </div>

            {/* Related Actions */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                🔗 Actions
              </h3>
              <div className="space-y-3">
                <button
                  onClick={() => window.location.reload()}
                  className="w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors text-sm"
                >
                  🔄 Refresh Analysis
                </button>
                <button
                  onClick={() => {
                    if (navigator.share) {
                      navigator
                        .share({
                          title: story.title,
                          url: window.location.href,
                        })
                        .catch(() => {
                          navigator.clipboard.writeText(window.location.href);
                        });
                    } else {
                      navigator.clipboard.writeText(window.location.href);
                    }
                  }}
                  className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors text-sm"
                >
                  📱 Share Story
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

StoryDetailPage.propTypes = {
  user: PropTypes.object,
  logout: PropTypes.func,
  renderUserInfo: PropTypes.func,
};

export default StoryDetailPage;
